/**
 * Shared helpers + config for job cards, rails, and the jobs grid.
 * Extracted from pages/Jobs.jsx so Jobs and Dashboard share one card system.
 */

export const CATEGORIES = [
  'All', 'Investment Banking', 'M&A', 'Private Equity', 'Hedge Fund', 'Markets',
  'Asset Management', 'Consulting', 'Corporate Banking', 'Fintech', 'Other',
];

const MS_DAY = 86400000;

export const daysUntil = (d) => (d ? Math.ceil((new Date(d) - new Date()) / MS_DAY) : null);

export const isClosingSoon = (d) => {
  const days = daysUntil(d);
  return days != null && days >= 0 && days <= 7;
};

export const daysLeft = (d) => {
  const days = daysUntil(d);
  if (days == null) return null;
  if (days < 0) return 'Closed';
  if (days === 0) return 'Closes today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
};

// Posted within the last 7 days
export const isNew = (d) => {
  if (!d) return false;
  const age = (Date.now() - new Date(d).getTime()) / MS_DAY;
  return age >= 0 && age <= 7;
};

export const matchColor = (s) => {
  if (s >= 75) return { bg: 'var(--tandm-success-bg)', color: 'var(--tandm-success-fg)', border: 'var(--tandm-success-border)' };
  if (s >= 50) return { bg: 'var(--tandm-warning-bg)', color: 'var(--tandm-warning-fg)', border: 'var(--tandm-warning-border)' };
  return { bg: 'var(--tandm-danger-bg)', color: 'var(--tandm-danger-fg)', border: 'var(--tandm-danger-border)' };
};

// Raw ring color (solid) by tier — used for the SVG match ring arc.
export const matchRingColor = (s) => {
  if (s >= 75) return 'var(--tandm-success-fg)';
  if (s >= 50) return 'var(--tandm-warning-fg)';
  return 'var(--tandm-danger-fg)';
};

/**
 * Pull up to `max` standout skills from a job's ai_notes for the hover reveal.
 * Merges technical/financial/soft skills, dedupes, falls back to key requirements.
 */
const normSkill = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();

// All distinct skill / requirement strings for a job, in source order.
export const collectSkills = (job) => {
  const n = job?.ai_notes;
  if (!n) return [];
  const pools = [n.technical_skills, n.financial_skills, n.soft_skills, n.key_requirements];
  const out = [];
  const seen = new Set();
  for (const pool of pools) {
    if (!Array.isArray(pool)) continue;
    for (const raw of pool) {
      const s = typeof raw === 'string' ? raw.trim() : '';
      if (!s) continue;
      const key = normSkill(s);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
  }
  return out;
};

// Frequency of each normalized skill across a set of jobs (for distinctiveness).
export const buildSkillFrequency = (jobs) => {
  const freq = new Map();
  for (const job of jobs || []) {
    for (const s of collectSkills(job)) {
      const k = normSkill(s);
      freq.set(k, (freq.get(k) || 0) + 1);
    }
  }
  return freq;
};

/**
 * Up to `max` skills for the hover. When a frequency map is supplied, prefer the
 * most DISTINCTIVE skills (rarest across the set) so cards don't all surface the
 * same common skills (Excel, Bloomberg, …); ties keep source order.
 */
export const whyItFits = (job, max = 3, freq = null) => {
  const skills = collectSkills(job);
  if (!freq || freq.size === 0) return skills.slice(0, max);
  // Rarest first (most distinctive); among equally-rare items prefer the shorter,
  // more display-friendly one; then source order.
  return skills
    .map((s, i) => ({ s, i, f: freq.get(normSkill(s)) ?? 1 }))
    .sort((a, b) => (a.f - b.f) || (a.s.length - b.s.length) || (a.i - b.i))
    .slice(0, max)
    .map((x) => x.s);
};

export const CAT = {
  'Investment Banking': {
    accent: '#4338CA', light: '#EEF2FF',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M8 10v11M12 10v11M16 10v11M20 10v11" />
      </svg>
    ),
  },
  'M&A': {
    accent: '#BE123C', light: '#FFF1F2',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 3v6a6 6 0 006 6v6M18 3v6a6 6 0 01-6 6" />
      </svg>
    ),
  },
  'Private Equity': {
    accent: '#7C3AED', light: '#F5F3FF',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18v16H3z" />
      </svg>
    ),
  },
  Consulting: {
    accent: '#065F46', light: '#ECFDF5',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  'Asset Management': {
    accent: '#92400E', light: '#FFFBEB',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  Fintech: {
    accent: '#4E6C7E', light: '#E8F1F3',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  'Hedge Fund': {
    accent: '#0F766E', light: '#F0FDFA',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  Markets: {
    accent: '#0891B2', light: '#ECFEFF',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 8-8M21 7v6M21 7h-6" />
      </svg>
    ),
  },
  'Corporate Banking': {
    accent: '#1D4ED8', light: '#EFF6FF',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M6 21V9l6-4 6 4v12M10 21v-5h4v5M9 12h.01M15 12h.01" />
      </svg>
    ),
  },
  Other: {
    accent: '#64748B', light: '#EEF2F4',
    icon: ({ color = 'currentColor' } = {}) => (
      <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={color} strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
};

export const SearchIcon = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);
export const LocationIcon = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
export const ClockIcon = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <circle cx="12" cy="12" r="10" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
  </svg>
);
export const CloseIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
export const SparkIcon = () => (
  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
export const ArrowRightIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

// Trademark notice shown at the foot of any page that renders company logos.
export const LOGO_TRADEMARK_NOTICE =
  'Company names and logos are trademarks of their respective owners. Tandm is not affiliated with or endorsed by these companies unless explicitly stated.';
